import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { DEVICE_LIST } from "@/data/devices";
import RentPickerModal from "@/components/RentPickerModal";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_anhanger-gear/artifacts/lhj2tq1t_Black%20Logo.png";

const deviceChildren = DEVICE_LIST.map((d) => ({
  name: d.shortTitle,
  href: `/geraete/${d.slug}`,
}));

const navigation = [
  { name: "Startseite", href: "/" },
  { name: "Anhänger mieten", href: "/anhaenger" },
  { name: "Geräte", href: "/geraete", children: deviceChildren },
  { name: "Kontakt", href: "/kontakt" },
];

export default function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const location = useLocation();

  const isActive = (href) => location.pathname === href;
  const isGroupActive = (item) =>
    item.href === location.pathname ||
    (item.children && item.children.some((c) => location.pathname === c.href)) ||
    (item.href === "/geraete" && location.pathname.startsWith("/geraete"));

  return (
    <header className="sticky top-0 z-50 w-full bg-white/95 backdrop-blur-md border-b border-zinc-200">
      <nav className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex h-20 items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center" data-testid="header-logo">
            <img src={LOGO_URL} alt="Rent Mich AT" className="h-16 sm:h-20 w-auto" />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navigation.map((item) =>
              item.children ? (
                <div key={item.name} className="relative group">
                  <Link
                    to={item.href}
                    data-testid="nav-geraete"
                    className={`flex items-center gap-1 text-sm font-medium transition-colors ${
                      isGroupActive(item) ? "text-orange-600" : "text-zinc-600 hover:text-zinc-900"
                    }`}
                  >
                    {item.name}
                    <ChevronDown className="h-4 w-4 transition-transform group-hover:rotate-180" />
                  </Link>
                  {/* Dropdown */}
                  <div className="absolute left-0 top-full pt-3 w-64 opacity-0 invisible translate-y-1 group-hover:opacity-100 group-hover:visible group-hover:translate-y-0 transition-all duration-200">
                    <div className="bg-white rounded-xl shadow-xl border border-zinc-200 p-2">
                      {item.children.map((child) => (
                        <Link
                          key={child.href}
                          to={child.href}
                          data-testid={`nav-dropdown-${child.href.split("/").pop()}`}
                          className={`block px-4 py-2.5 rounded-lg text-sm transition-colors ${
                            isActive(child.href)
                              ? "bg-orange-50 text-orange-600 font-medium"
                              : "text-zinc-700 hover:bg-zinc-50"
                          }`}
                        >
                          {child.name}
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <Link
                  key={item.name}
                  to={item.href}
                  data-testid={`nav-${item.href.replace("/", "") || "home"}`}
                  className={`text-sm font-medium transition-colors ${
                    isActive(item.href) ? "text-orange-600" : "text-zinc-600 hover:text-zinc-900"
                  }`}
                >
                  {item.name}
                </Link>
              )
            )}
          </div>

          {/* Desktop CTA */}
          <div className="hidden md:flex items-center gap-4">
            <Button
              data-testid="header-cta-btn"
              onClick={() => setPickerOpen(true)}
              className="bg-orange-600 hover:bg-orange-700 text-white px-6"
            >
              Jetzt Verfügbarkeit prüfen
            </Button>
          </div>

          {/* Mobile Menu */}
          <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
            <SheetTrigger asChild className="md:hidden">
              <Button variant="ghost" size="icon" data-testid="mobile-menu-btn">
                <Menu className="h-6 w-6" />
                <span className="sr-only">Menü öffnen</span>
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[280px] bg-white overflow-y-auto">
              <div className="flex flex-col gap-5 mt-8">
                {navigation.map((item) =>
                  item.children ? (
                    <div key={item.name} className="flex flex-col gap-3">
                      <Link
                        to={item.href}
                        onClick={() => setMobileMenuOpen(false)}
                        className={`text-lg font-medium transition-colors ${
                          isGroupActive(item) ? "text-orange-600" : "text-zinc-600 hover:text-zinc-900"
                        }`}
                      >
                        {item.name}
                      </Link>
                      <div className="flex flex-col gap-3 pl-4 border-l-2 border-zinc-100">
                        {item.children.map((child) => (
                          <Link
                            key={child.href}
                            to={child.href}
                            onClick={() => setMobileMenuOpen(false)}
                            className={`text-base transition-colors ${
                              isActive(child.href) ? "text-orange-600 font-medium" : "text-zinc-500 hover:text-zinc-900"
                            }`}
                          >
                            {child.name}
                          </Link>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <Link
                      key={item.name}
                      to={item.href}
                      onClick={() => setMobileMenuOpen(false)}
                      className={`text-lg font-medium transition-colors ${
                        isActive(item.href) ? "text-orange-600" : "text-zinc-600 hover:text-zinc-900"
                      }`}
                    >
                      {item.name}
                    </Link>
                  )
                )}
                <Button
                  onClick={() => {
                    setMobileMenuOpen(false);
                    setPickerOpen(true);
                  }}
                  className="w-full bg-orange-600 hover:bg-orange-700 text-white mt-4"
                >
                  Jetzt Verfügbarkeit prüfen
                </Button>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </nav>

      <RentPickerModal open={pickerOpen} onOpenChange={setPickerOpen} />
    </header>
  );
}
